import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import CopyButton from '../components/CopyButton';
import PaymentAuthorizationModal from '../components/PaymentAuthorizationModal';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Clock, Play, Target, DollarSign,
  TrendingUp, AlertTriangle, Check, X, RefreshCw, ListChecks, CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Actions = () => {
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedAction, setSelectedAction] = useState(null);
  const [paymentAuth, setPaymentAuth] = useState(null);

  useEffect(() => {
    loadActions();
  }, [activeTab]);

  const loadActions = async () => {
    try {
      setLoading(true);
      const statusFilter = activeTab === 'all' ? null : activeTab;
      const url = statusFilter ? `${API}/actions?status=${statusFilter}` : `${API}/actions`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setActions(res.data.actions || []);
    } catch (error) {
      console.error('Failed to load actions:', error);
      toast.error('Failed to load actions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (actionId) => {
    try {
      await axios.post(`${API}/actions/${actionId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Action approved!');
      loadActions();
    } catch (error) {
      toast.error('Failed to approve action');
    }
  };

  const handleReject = async (actionId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;

    try {
      await axios.post(`${API}/actions/${actionId}/reject`, { reason }, { headers: getAuthHeaders() });
      toast.success('Action rejected');
      loadActions();
    } catch (error) {
      toast.error('Failed to reject action');
    }
  };

  const handleStart = async (actionId) => {
    try {
      await axios.post(`${API}/actions/${actionId}/start`, {}, { headers: getAuthHeaders() });
      toast.success('Action started!');
      loadActions();
    } catch (error) {
      toast.error('Failed to start action');
    }
  };

  const handleStepToggle = async (actionId, stepId, completed) => {
    try {
      await axios.post(
        `${API}/actions/${actionId}/steps/${stepId}`,
        { step_id: stepId, completed: !completed },
        { headers: getAuthHeaders() }
      );
      toast.success(completed ? 'Step unchecked' : 'Step completed!');
      loadActions();
    } catch (error) {
      toast.error('Failed to update step');
    }
  };

  const handleAuthorizePayment = async (action) => {
    try {
      const res = await axios.post(
        `${API}/payments/create-authorization`,
        {
          action_id: action.action_id,
          payee: 'Payment Recipient',
          amount: action.required_capital,
          source_account: 'Primary Account (****1234)',
          reason: `Payment for action: ${action.title}`,
          metadata: { action_id: action.action_id }
        },
        { headers: getAuthHeaders() }
      );
      setPaymentAuth(res.data);
      toast.success('Payment authorization created');
    } catch (error) {
      console.error('Failed to create payment authorization:', error);
      toast.error('Failed to create payment authorization');
    }
  };

  const handleApprovePayment = async (authorizationId) => {
    try {
      await axios.post(
        `${API}/payments/approve/${authorizationId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('✅ Payment authorized (no execution)');
      setPaymentAuth(null);
    } catch (error) {
      console.error('Failed to approve payment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to approve payment';
      toast.error(errorMsg);
    }
  };

  const handleCancelPayment = async (authorizationId, reason) => {
    try {
      await axios.post(
        `${API}/payments/cancel/${authorizationId}`,
        { reason },
        { headers: getAuthHeaders() }
      );
      toast.success('Payment authorization cancelled');
      setPaymentAuth(null);
    } catch (error) {
      console.error('Failed to cancel payment:', error);
      toast.error('Failed to cancel payment authorization');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-gray-400',
      pending: 'text-yellow-400',
      approved: 'text-green-400',
      rejected: 'text-red-400',
      in_progress: 'text-blue-400',
      completed: 'text-emerald-400'
    };
    return colors[status] || 'text-gray-400';
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <Clock className="w-5 h-5" />,
      pending: <AlertTriangle className="w-5 h-5" />,
      approved: <CheckCircle className="w-5 h-5" />,
      rejected: <XCircle className="w-5 h-5" />,
      in_progress: <Play className="w-5 h-5" />,
      completed: <Check className="w-5 h-5" />
    };
    return icons[status] || <Clock className="w-5 h-5" />;
  };

  const tabs = [
    { key: 'pending', label: 'Pending Approval', count: actions.filter(a => a.status === 'pending').length },
    { key: 'approved', label: 'Approved', count: actions.filter(a => a.status === 'approved').length },
    { key: 'in_progress', label: 'In Progress', count: actions.filter(a => a.status === 'in_progress').length },
    { key: 'completed', label: 'Completed', count: actions.filter(a => a.status === 'completed').length },
    { key: 'draft', label: 'Draft', count: actions.filter(a => a.status === 'draft').length }
  ];

  if (loading && actions.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-12 h-12 animate-spin text-[#F59E0B]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3" data-testid="actions-page">
              <Target className="text-[#F59E0B]" />
              Action Packets
            </h1>
            <p className="text-gray-400 mt-1">Approved execution plans with step tracking</p>
          </div>
          <button
            onClick={loadActions}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
            data-testid="refresh-actions-btn"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.key
                  ? 'bg-[#F59E0B] text-black font-semibold'
                  : 'bg-[#1C1C1E] text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key ? 'bg-black/20' : 'bg-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Actions List */}
        <div className="space-y-4">
          {actions.length > 0 ? (
            actions.map((action) => (
              <div
                key={action.action_id}
                className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6 hover:border-[#F59E0B]/50 transition-colors"
                data-testid="action-card"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">{action.title}</h3>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusColor(action.status)}`}>
                        {getStatusIcon(action.status)}
                        <span className="capitalize">{action.status.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm">{action.summary}</p>
                  </div>
                  <CopyButton text={`${action.title}\n\n${action.summary}\n\nSteps:\n${action.steps.map(s => `- ${s.description}`).join('\n')}`} />
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-[#0A0A0A] rounded-lg">
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Required Capital</div>
                    <div className="text-white font-semibold">${action.required_capital.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Timeline</div>
                    <div className="text-white font-medium">{action.timeline}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Progress</div>
                    <div className="text-[#F59E0B] font-semibold">
                      {action.progress_percentage.toFixed(0)}% ({action.completed_steps}/{action.total_steps} steps)
                    </div>
                  </div>
                </div>

                {/* Expected Returns */}
                {action.expected_return && Object.keys(action.expected_return).length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-red-500/10 border border-red-500/30 rounded">
                      <div className="text-red-400 text-xs mb-1">Conservative</div>
                      <div className="text-white font-medium">${action.expected_return.conservative?.toLocaleString() || 0}</div>
                    </div>
                    <div className="text-center p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <div className="text-yellow-400 text-xs mb-1">Base Case</div>
                      <div className="text-white font-medium">${action.expected_return.base?.toLocaleString() || 0}</div>
                    </div>
                    <div className="text-center p-2 bg-green-500/10 border border-green-500/30 rounded">
                      <div className="text-green-400 text-xs mb-1">Upside</div>
                      <div className="text-white font-medium">${action.expected_return.upside?.toLocaleString() || 0}</div>
                    </div>
                  </div>
                )}

                {/* Steps Checklist (for in_progress actions) */}
                {action.status === 'in_progress' && action.steps && action.steps.length > 0 && (
                  <div className="border-t border-gray-700 pt-4 mb-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-white mb-3">
                      <ListChecks className="w-4 h-4" />
                      Execution Steps
                    </div>
                    <div className="space-y-2">
                      {action.steps.map((step, idx) => (
                        <div key={step.step_id} className="flex items-start gap-3">
                          <button
                            onClick={() => handleStepToggle(action.action_id, step.step_id, step.completed)}
                            className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              step.completed
                                ? 'bg-[#F59E0B] border-[#F59E0B]'
                                : 'border-gray-600 hover:border-[#F59E0B]'
                            }`}
                            data-testid={`step-checkbox-${idx}`}
                          >
                            {step.completed && <Check className="w-3 h-3 text-black" />}
                          </button>
                          <div className="flex-1">
                            <p className={`text-sm ${step.completed ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                              {step.description}
                            </p>
                            {step.notes && (
                              <p className="text-xs text-gray-500 mt-1">{step.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Risks */}
                {action.risks && action.risks.length > 0 && (
                  <div className="border-t border-gray-700 pt-4 mb-4">
                    <div className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      Risks
                    </div>
                    <ul className="space-y-1">
                      {action.risks.slice(0, 3).map((risk, idx) => (
                        <li key={idx} className="text-sm text-gray-400 flex items-start gap-2">
                          <span className="text-yellow-400">•</span>
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {action.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(action.action_id)}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center gap-2 font-semibold"
                        data-testid="approve-btn"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(action.action_id)}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center justify-center gap-2 font-semibold"
                        data-testid="reject-btn"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </>
                  )}
                  {action.status === 'approved' && (
                    <>
                      <button
                        onClick={() => handleStart(action.action_id)}
                        className="flex-1 px-4 py-2 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center justify-center gap-2 font-semibold"
                        data-testid="start-action-btn"
                      >
                        <Play className="w-4 h-4" />
                        Start Execution
                      </button>
                      <button
                        onClick={() => handleAuthorizePayment(action)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2"
                        data-testid="authorize-payment-btn"
                      >
                        <CreditCard className="w-4 h-4" />
                        Authorize Payment
                      </button>
                    </>
                  )}
                  {action.status === 'rejected' && action.rejection_reason && (
                    <div className="flex-1 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                      <strong>Rejected:</strong> {action.rejection_reason}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-12 text-center">
              <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">No actions in {activeTab.replace('_', ' ')} status</p>
              <p className="text-gray-500 text-sm">Create actions from opportunities to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Authorization Modal */}
      {paymentAuth && (
        <PaymentAuthorizationModal
          authorization={paymentAuth}
          onApprove={handleApprovePayment}
          onCancel={handleCancelPayment}
          onClose={() => setPaymentAuth(null)}
        />
      )}
    </Layout>
  );
};

export default Actions;
